import React from "react";
import { Link } from "react-router-dom";
import CTABanner from "../components/CTABanner";
import LegalSignoff from "../components/LegalSignoff";

const Section = ({ heading, children }) => (
  <div className="mt-10">
    {heading && <h2 className="ek-heading text-2xl md:text-3xl text-black mb-4">{heading}</h2>}
    <div className="ek-body space-y-4 text-[16.5px]">{children}</div>
  </div>
);

const Cookies = () => {
  return (
    <>
      <section className="ek-gradient-dark text-white relative overflow-hidden">
        <div className="ek-star-watermark" />
        <div className="ek-container relative z-10 py-20 md:py-28">
          <div className="ek-eyebrow text-[#A179FA]">Legal</div>
          <h1 className="ek-heading text-4xl md:text-5xl lg:text-6xl text-white mt-4 max-w-4xl leading-tight" data-testid="cookies-headline">
            Ekselens Consulting Cookie Policy
          </h1>
          <p className="text-zinc-200 font-light text-lg mt-6 max-w-3xl">
            How and why we use cookies and similar technologies on the Ekselens Consulting website.
          </p>
        </div>
      </section>

      <section className="bg-white py-16 md:py-20">
        <div className="ek-container max-w-4xl">
          <p className="ek-body text-lg">
            This Cookie Policy explains what cookies are, how Ekselens Consulting ("we", "us", "our") uses them on www.ekselens.com, and the choices available to you. This policy should be read alongside our <Link to="/privacy" className="text-[#7737F6] hover:underline">Privacy Policy</Link>.
          </p>

          <Section heading="What are cookies?">
            <p>
              Cookies are small text files placed on your device when you visit a website. They are widely used to make websites work, improve user experience, and provide analytics information to site owners.
            </p>
            <p>
              We also use similar technologies such as local storage, pixels and tags, which we refer to collectively as "cookies" throughout this policy.
            </p>
          </Section>

          <Section heading="The cookies we use">
            <p>Ekselens currently uses a small, deliberate set of analytics tools:</p>
            <ul className="list-disc list-inside space-y-3 ml-2">
              <li>
                <strong>Strictly necessary</strong> — local storage to remember whether you accepted this notice and your admin sign-in session if you are an admin user. These cannot be disabled.
              </li>
              <li>
                <strong>Plausible Analytics (cookieless, always on)</strong> — aggregated, anonymous traffic counts. Plausible does not set cookies, does not track you across other sites, and never sells your data. We use it to understand which pages help visitors and which need to be improved.
              </li>
              <li>
                <strong>Microsoft Clarity (optional, gated by consent)</strong> — when you accept analytics in our consent banner, we may enable Clarity to record session heatmaps and (in some cases) replays. This uses cookies. You can opt out at any time by clicking "Essential only" or clearing your browser storage. We do not enable Clarity unless you have explicitly accepted.
              </li>
              <li>
                <strong>Functional cookies</strong> — remember choices you make (such as the area of interest selected in our enquiry form) to give you a more personalised experience.
              </li>
              <li>
                <strong>Marketing cookies</strong> — we do not currently use marketing cookies for retargeting or advertising. If this changes, we will update this policy and request your consent.
              </li>
            </ul>
          </Section>

          <Section heading="Third-party cookies">
            <p>
              Some cookies are placed by third parties whose services we use, such as analytics or email delivery providers. These third parties have their own privacy and cookie policies, which we recommend you review.
            </p>
          </Section>

          <Section heading="Managing your cookie preferences">
            <p>
              You can control and delete cookies through your browser settings. Most browsers allow you to refuse cookies, delete existing cookies, or be notified when a cookie is being set. Instructions are available in your browser's help section.
            </p>
            <p>
              Please note that disabling certain cookies may affect the functionality of the site, including the discovery-conversation enquiry form.
            </p>
          </Section>

          <Section heading="Updates to this policy">
            <p>
              We may update this Cookie Policy from time to time to reflect changes in technology, regulation or our practices. Any changes will be posted on this page with a revised date below.
            </p>
          </Section>

          <Section heading="Contact us">
            <p>
              If you have any questions about our use of cookies, please email us at <a href="mailto:info@ekselens.com" className="text-[#7737F6] hover:underline">info@ekselens.com</a>.
            </p>
          </Section>

          <LegalSignoff page="cookies" />
        </div>
      </section>

      <CTABanner />
    </>
  );
};

export default Cookies;
